import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';
import { AppointmentEvent, AppointmentsResponse } from '../types/appointment';
import { renderText } from '../utils/ui-text';
import useBackendApi from '../hooks/useBackendApi';
import GradientCircularProgress from '../components/common/GradientCircularProgress';
import { formatEventDateTime } from '../utils/dateTime';

const backendURL = process.env.REACT_APP_BACKEND_URL;

type AppointmentEventCardProps = {
  event: AppointmentEvent;
};

export const AppointmentEventCard: React.FC<AppointmentEventCardProps> = ({ event }) => {
  const { theme } = useThemeContext();
  const eventCardSx = {
    border: '1px solid',
    borderColor: theme.custom.pages.frost.authHome.cards.borderColor,
    backgroundColor: theme.custom.pages.frost.authHome.cards.event.backgroundColor,
    padding: '18px',
    borderRadius: '16px',
    marginTop: '14px',
  };

  return (
    <Box sx={eventCardSx}>
      <Typography sx={{ fontWeight: 500, color: theme.custom.pages.frost.authHome.typography.body1.color }}>
        {formatEventDateTime(event.event_start_date, event.event_end_date)}
      </Typography>

      <Typography
        variant="subtitle1"
        sx={{
          color: theme.custom.pages.frost.authHome.typography.body2.color,
          fontWeight: 300,
          marginTop: '2px',
        }}
      >
        {event.location_name}
      </Typography>

      {renderText(theme.custom.pages.frost.authHome.typography.body1.color, event.description)}
    </Box>
  );
};

type AppointmentsListProps = {
  limit?: number;
};

const AppointmentsList: React.FC<AppointmentsListProps> = ({ limit }) => {
  const { theme } = useThemeContext();
  const { apiFetchRequest } = useBackendApi();

  const [events, setEvents] = useState<AppointmentEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchAppointments = async () => {
      setLoading(true);
      try {
        const endpoint = `${backendURL}/users/appointments${limit == null ? '' : `?limit=${limit}`}`;
        const response = await apiFetchRequest({
          endpoint,
          method: 'GET',
        });

        if (response?.status !== 200) {
          console.warn(`Unexpected appointments status: ${response?.status}`);
          setEvents([]);
          setNotFound(true);
        } else {
          const body = response?.body as AppointmentsResponse | undefined;
          const evts = body?.events ?? [];
          setEvents(evts);
          setNotFound(!evts.length);
        }
      } catch (error) {
        console.error('Failed to fetch appointments:', error);
        setEvents([]);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [apiFetchRequest, limit]);

  const cardSx = {
    backgroundColor: theme.custom.pages.frost.authHome.cards.backgroundColor,
    borderRadius: '16px',
    padding: { sm: '28px', xs: '20px' },
    border: '1px solid',
    borderColor: theme.custom.pages.frost.authHome.cards.borderColor,
  };

  const sectionTitleSx = {
    fontWeight: 800,
    letterSpacing: '-0.02em',
    color: theme.custom.pages.frost.authHome.typography.body1.color,
  };

  const subHeaderSx = {
    color: theme.custom.pages.frost.authHome.typography.body2.color,
    fontWeight: 600,
    marginTop: '10px',
  };

  return (
    <Box sx={cardSx}>
      <Typography variant="h5" sx={sectionTitleSx}>
        Appointments
      </Typography>

      {loading ? (
        <Box sx={{ marginTop: '14px' }}>
          <GradientCircularProgress
            loadingMessage="Loading appointments"
            additionalCircularSxProps={{
              width: '20px !important',
              height: '20px !important',
              margin: '16px',
            }}
            containerSxProps={{
              width: '300px !important',
              height: '100px !important',
              display: 'inline-flex',
            }}
          />
        </Box>
      ) : notFound ? (
        <Box sx={{ marginTop: '14px' }}>
          <Typography variant="body2" sx={{ color: theme.custom.pages.frost.authHome.typography.body2.color }}>
            No upcoming appointments found.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ marginTop: '12px' }}>
          <Typography variant="subtitle1" sx={subHeaderSx}>
            Upcoming appointments
          </Typography>

          {events.map((evt) => (
            <AppointmentEventCard
              key={evt.acc_event_id ?? `${evt.event_start_date}-${evt.title}-${evt.location_name}`}
              event={evt}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AppointmentsList;
